// LongPressPicker — sectioned list of nearby features that appears
// after the user long-presses anywhere on the map. ForeFlight calls
// this the "long-press info" menu.
//
// Sections:
//   * Airports    (up to 4 within 25 nm)
//   * Navaids     (up to 3 within 25 nm)
//   * Fixes       (up to 4 within 15 nm)
//   * Airspace    (whatever polygons cover this point — SUA, TFR,
//                  AIRMET, SIGMET — not capped, sorted by ceiling
//                  lowest first so the most-restrictive shows up
//                  first)
//   * Coordinates (always present — the raw lat/lon of the press
//                  point, so the pilot can Direct-To an arbitrary
//                  point on the chart)
//
// Tapping a row's body drills into the right detail card via the
// parent. Tapping the right-side ➤ button triggers Direct-To, which
// replaces the active route with [GPS-or-fallback → row target].

import { useEffect, useMemo, useRef, useState } from 'react';
import { Flag, Plus, MoveDown, Droplet } from '../shell/Icon';
import { SearchHit } from '../lib/search';
import type { AirspaceKind } from '../airport/AirspaceDetail';
import { Icon } from '../shell/Icon';
import { useWaypointStore, defaultWaypointName } from '../stores/waypointStore';
import { usePlanStore } from '../stores/planStore';

export interface AirspaceHit {
  kind: 'airspace';
  airspaceKind: AirspaceKind;
  /** Display title — e.g. "R-2502N · Restricted" or "TFR 6/0123 · STADIUM" */
  title: string;
  subtitle: string;
  /** Original GeoJSON properties, forwarded to the detail card. */
  properties: Record<string, unknown>;
}

export type LongPressHit = SearchHit | AirspaceHit;

/** Synthetic "tap coordinates" target used for Direct-To-to-point. */
export interface CoordTarget {
  kind: 'coord';
  lat: number;
  lon: number;
}

/** Any row the picker can fire Direct-To against. Airspace polygons
 *  are intentionally excluded — you can't go Direct to an airspace. */
export type DirectToTarget = SearchHit | CoordTarget;

interface Props {
  /** All feature hits in one array; we group by `kind` here. */
  hits: LongPressHit[];
  /** Long-press point in canvas-relative pixels. */
  point: { x: number; y: number };
  /** Geo coordinate of the press, used for the COORDINATES row + as
   *  the lat/lon for "Direct To this point". */
  lngLat: [number, number];
  onPick: (h: LongPressHit) => void;
  /** Fires when the user taps the ➤ Direct-To button on a row. The
   *  parent is responsible for resolving start (GPS / route origin)
   *  and committing the plan via planStore.directTo(). */
  onDirectTo: (target: DirectToTarget) => void;
  /** Fires when the user taps the dip-site droplet button on the
   *  coordinates row. Parent hosts the DipSiteRecorder modal. */
  onRecordDip?: (latLon: { lat: number; lon: number }) => void;
  onClose: () => void;
  loading?: boolean;
}

const KIND_DOT: Record<string, string> = {
  airport: 'bg-ember-500',
  navaid: 'bg-cyan-400',
  fix: 'bg-violet-400',
  airspace: 'bg-amber-500',
  coord: 'bg-cockpit-200',
};

const SECTION_LABEL: Record<string, string> = {
  airport: 'AIRPORTS',
  navaid: 'NAVAIDS',
  fix: 'FIXES',
  airspace: 'AIRSPACE',
  coord: 'COORDINATES',
};

export default function LongPressPicker({
  hits, point, lngLat, onPick, onDirectTo, onRecordDip, onClose, loading,
}: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const addWaypoint = useWaypointStore((s) => s.add);
  // Subscribe to the raw list (stable reference) and derive nearby /
  // nearest with useMemo. Subscribing directly to `s.within(...)` or
  // `s.nearest(...)` returns a freshly-allocated array/object on every
  // store tick, which trips Zustand's `===` equality check and forces
  // an infinite re-render loop — the long-press picker would crash
  // the app on desktop the moment it mounted (Aaron field report
  // 2026-05-13 23:28 PT).
  const waypointList = useWaypointStore((s) => s.list);
  const nearbyWaypoints = useMemo(
    () => useWaypointStore.getState().within(lngLat[1], lngLat[0], 25),
    // recompute on list mutation or geo change
    [waypointList, lngLat[0], lngLat[1]],
  );
  const nearestWpt = useMemo(
    () => useWaypointStore.getState().nearest(lngLat[1], lngLat[0], 0.01),
    [waypointList, lngLat[0], lngLat[1]],
  );
  const addToPlan = usePlanStore((s) => s.add);
  const insertAtPlan = usePlanStore((s) => s.insertAt);
  const planRoute = usePlanStore((s) => s.route);

  // Insert popover state for the coordinates row
  const [insertOpen, setInsertOpen] = useState(false);
  const insertBtnRef = useRef<HTMLButtonElement>(null);

  // Inline save-waypoint prompt state
  const [savingWpt, setSavingWpt] = useState(false);
  const [wptName, setWptName] = useState(() => defaultWaypointName(lngLat[1], lngLat[0]));

  function handleSaveWaypoint() {
    setSavingWpt(true);
  }

  async function handleConfirmSave() {
    await addWaypoint({ name: wptName.trim() || defaultWaypointName(lngLat[1], lngLat[0]), lat: lngLat[1], lon: lngLat[0] });
    setSavingWpt(false);
    onClose();
  }

  function handleAddToPlan() {
    const name = nearestWpt?.name ?? defaultWaypointName(lngLat[1], lngLat[0]);
    addToPlan({ ident: name, lat: lngLat[1], lon: lngLat[0], kind: 'user' });
    onClose();
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    function onDocPointer(e: PointerEvent) {
      const node = ref.current;
      if (!node || node.contains(e.target as Node)) return;
      onClose();
    }
    window.addEventListener('keydown', onKey);
    // Defer outside-click attach so the long-press release that opened
    // us doesn't immediately close us.
    const t = window.setTimeout(() => {
      window.addEventListener('pointerdown', onDocPointer, true);
    }, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDocPointer, true);
    };
  }, [onClose]);

  // Group hits by kind, preserving the input order within each group.
  const groups: Record<string, LongPressHit[]> = {
    airport: [], navaid: [], fix: [], airspace: [],
  };
  for (const h of hits) {
    if (h.kind in groups) groups[h.kind].push(h);
  }
  const sectionOrder = ['airport', 'navaid', 'fix', 'airspace'] as const;

  const coordTarget: CoordTarget = { kind: 'coord', lat: lngLat[1], lon: lngLat[0] };
  const coordTitle = formatCoord(lngLat[1], lngLat[0]);

  // Anchor near the press: bias up-and-right, clamp to viewport.
  const W = 300;
  const maxH = 480;
  const margin = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = point.x + 14;
  if (left + W + margin > vw) left = Math.max(margin, point.x - W - 14);
  let top = point.y - 8;
  if (top + maxH + margin > vh) top = Math.max(margin, vh - maxH - margin);
  if (top < margin) top = margin;

  // We always show something now — at minimum the Coordinates row —
  // so the "nothing within reach" empty state no longer fires.
  return (
    <div
      ref={ref}
      style={{ position: 'absolute', left, top, width: W }}
      className="z-30 bg-cockpit-800/95 backdrop-blur border border-cockpit-600 rounded-xl shadow-rail overflow-hidden"
      role="dialog"
      aria-label="Nearby features"
    >
      <div className="px-3 py-2 border-b border-cockpit-700 flex items-center justify-between">
        <span className="text-[11px] tracking-widest text-cockpit-300">
          NEARBY
        </span>
        <button
          onClick={onClose}
          aria-label="Close"
          className="text-cockpit-300 hover:text-cockpit-100 text-sm leading-none"
        >✕</button>
      </div>
      {loading && (
        <div className="px-3 py-1.5 text-[11px] text-cockpit-400">Looking nearby…</div>
      )}
      <div className="max-h-[420px] overflow-y-auto scroll-touch">
        {sectionOrder.map((kind) => {
          const items = groups[kind];
          if (items.length === 0) return null;
          return (
            <section key={kind}>
              <div className="px-3 py-1 bg-cockpit-700/40 border-y border-cockpit-700 text-[10px] tracking-widest text-cockpit-300">
                {SECTION_LABEL[kind]}
              </div>
              <ul className="divide-y divide-cockpit-700">
                {items.map((h, i) => {
                  // Airspace polygons can't be a Direct-To target.
                  const canDirect = h.kind !== 'airspace';
                  return (
                    <li key={`${kind}-${i}`} className="flex items-stretch">
                      <button
                        type="button"
                        onClick={() => onPick(h)}
                        className="flex-1 min-w-0 text-left px-3 py-2 hover:bg-cockpit-700 active:bg-cockpit-700 flex items-center gap-2"
                      >
                        <span
                          className={`inline-block h-2.5 w-2.5 rounded-full ${KIND_DOT[kind]} shrink-0`}
                          aria-hidden
                        />
                        <span className="flex flex-col min-w-0 flex-1">
                          <span className="font-mono text-sm text-cockpit-100 truncate">
                            {rowTitle(h)}
                          </span>
                          <span className="text-[11px] text-cockpit-300 truncate">
                            {rowSubtitle(h)}
                          </span>
                        </span>
                      </button>
                      {canDirect && (
                        <DirectToButton
                          onClick={() => onDirectTo(h as SearchHit)}
                          label={`Direct to ${(h as SearchHit).ident}`}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}

        {/* Saved waypoints near this press point (within 25 nm, max 4). */}
        {nearbyWaypoints.length > 0 && (
          <section>
            <div className="px-3 py-1 bg-cockpit-700/40 border-y border-cockpit-700 text-[10px] tracking-widest text-cockpit-300">
              SAVED WAYPOINTS
            </div>
            <ul className="divide-y divide-cockpit-700">
              {nearbyWaypoints.slice(0, 4).map((w) => (
                <li key={w.id} className="flex items-stretch">
                  <div className="flex-1 min-w-0 px-3 py-2 flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full bg-ember-400 shrink-0" aria-hidden />
                    <span className="flex flex-col min-w-0 flex-1">
                      <span className="font-mono text-sm text-cockpit-100 truncate">{w.name}</span>
                      <span className="text-[11px] text-cockpit-300 truncate">{w.distanceNm.toFixed(1)} nm</span>
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => { addToPlan({ ident: w.name, lat: w.lat, lon: w.lon, kind: 'user' }); onClose(); }}
                    aria-label={`Add ${w.name} to plan`}
                    title="Add to plan"
                    className="shrink-0 w-10 flex items-center justify-center border-l border-cockpit-700 text-cockpit-300 hover:bg-cockpit-700/50"
                  >
                    <Plus size={15} />
                  </button>
                  <DirectToButton
                    onClick={() => onDirectTo({ kind: 'coord', lat: w.lat, lon: w.lon })}
                    label={`Direct to ${w.name}`}
                  />
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Coordinates row — always present. Lets the pilot Direct-To
            an arbitrary point on the chart without first dropping a
            user waypoint. */}
        <section>
          <div className="px-3 py-1 bg-cockpit-700/40 border-y border-cockpit-700 text-[10px] tracking-widest text-cockpit-300">
            {SECTION_LABEL.coord}
          </div>
          <ul className="divide-y divide-cockpit-700">
            <li className="flex items-stretch">
              <div className="flex-1 min-w-0 px-3 py-2 flex items-center gap-2">
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${KIND_DOT.coord} shrink-0`}
                  aria-hidden
                />
                <span className="flex flex-col min-w-0 flex-1">
                  <span className="font-mono text-sm text-cockpit-100 truncate">
                    {coordTitle}
                  </span>
                  <span className="text-[11px] text-cockpit-300 truncate">
                    Tap point — save as a user waypoint
                  </span>
                </span>
              </div>
              {/* Save waypoint button — labeled (was icon-only; Wiley
                  feedback 2026-05-13 said the gesture wasn't discoverable). */}
              <button
                type="button"
                onClick={handleSaveWaypoint}
                aria-label="Save as custom waypoint"
                title="Save waypoint"
                className="shrink-0 flex items-center gap-1 px-3 border-l border-cockpit-700 text-ember-300 hover:bg-ember-600/20 font-medium text-xs"
              >
                <Flag size={13} />
                <span>Save</span>
              </button>
              {/* Record dip site button — only visible when host wired it */}
              {onRecordDip && (
                <button
                  type="button"
                  onClick={() => { onRecordDip({ lat: lngLat[1], lon: lngLat[0] }); onClose(); }}
                  aria-label="Record dip site at this location"
                  title="Record dip site"
                  className="shrink-0 w-10 flex items-center justify-center border-l border-cockpit-700 text-cyan-400 hover:bg-cyan-600/20"
                >
                  <Droplet size={15} />
                </button>
              )}
              {/* Add to plan button (append) */}
              <button
                type="button"
                onClick={handleAddToPlan}
                aria-label="Add to flight plan"
                title="Add to plan"
                className="shrink-0 w-10 flex items-center justify-center border-l border-cockpit-700 text-cockpit-300 hover:bg-cockpit-700/50"
              >
                <Plus size={15} />
              </button>
              {/* Insert at position button — only shown when route has ≥1 point */}
              {planRoute.length >= 1 && (
                <div className="relative">
                  <button
                    ref={insertBtnRef}
                    type="button"
                    onClick={() => setInsertOpen((v) => !v)}
                    aria-label="Insert into route at position"
                    title="Insert at position…"
                    className="shrink-0 w-10 h-full flex items-center justify-center border-l border-cockpit-700 text-cockpit-300 hover:bg-cockpit-700/50"
                  >
                    <MoveDown size={13} />
                  </button>
                  {insertOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setInsertOpen(false)}
                        aria-hidden
                      />
                      <div className="absolute bottom-full right-0 mb-1 z-50 w-56 bg-cockpit-800 border border-cockpit-600 rounded-xl shadow-rail overflow-hidden">
                        <div className="px-3 py-1.5 border-b border-cockpit-700 text-[10px] tracking-widest text-cockpit-300">
                          INSERT INTO ROUTE
                        </div>
                        <ul className="divide-y divide-cockpit-700 max-h-48 overflow-y-auto">
                          {[{ label: `Before ${planRoute[0].ident}`, idx: 0 }]
                            .concat(
                              planRoute.slice(1).map((_, i) => ({
                                label: `Between ${planRoute[i].ident} and ${planRoute[i + 1].ident}`,
                                idx: i + 1,
                              }))
                            )
                            .concat([{ label: `After ${planRoute[planRoute.length - 1].ident}`, idx: planRoute.length }])
                            .map((g) => (
                              <li key={g.idx}>
                                <button
                                  className="w-full text-left px-3 py-2 text-xs text-cockpit-100 hover:bg-cockpit-700"
                                  onClick={() => {
                                    const name = nearestWpt?.name ?? defaultWaypointName(lngLat[1], lngLat[0]);
                                    insertAtPlan(g.idx, { ident: name, lat: lngLat[1], lon: lngLat[0], kind: 'user' });
                                    setInsertOpen(false);
                                    onClose();
                                  }}
                                >
                                  {g.label}
                                </button>
                              </li>
                            ))}
                        </ul>
                      </div>
                    </>
                  )}
                </div>
              )}
              <DirectToButton
                onClick={() => onDirectTo(coordTarget)}
                label="Direct to tapped point"
              />
            </li>
            {/* Inline save prompt */}
            {savingWpt && (
              <li className="px-3 py-2 space-y-2 border-t border-cockpit-700 bg-cockpit-800/60">
                <p className="text-[11px] text-cockpit-400">Waypoint name</p>
                <div className="flex gap-2">
                  <input
                    autoFocus
                    type="text"
                    value={wptName}
                    onChange={(e) => setWptName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleConfirmSave(); if (e.key === 'Escape') setSavingWpt(false); }}
                    className="flex-1 bg-cockpit-900 border border-cockpit-600 rounded px-2 py-1 text-sm text-cockpit-100 focus:outline-none focus:border-ember-500"
                  />
                  <button
                    type="button"
                    onClick={() => void handleConfirmSave()}
                    className="px-3 py-1 rounded bg-ember-600 hover:bg-ember-500 text-xs text-white"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setSavingWpt(false)}
                    className="px-2 py-1 rounded border border-cockpit-600 text-xs text-cockpit-300 hover:text-cockpit-100"
                  >
                    Cancel
                  </button>
                </div>
              </li>
            )}
          </ul>
        </section>
      </div>
    </div>
  );
}

function DirectToButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title="Direct To"
      className="shrink-0 w-12 flex items-center justify-center border-l border-cockpit-700 text-ember-400 hover:bg-ember-600/20 active:bg-ember-600/30"
    >
      <Icon name="direct-to" className="w-4 h-4" />
    </button>
  );
}

function rowTitle(h: LongPressHit): string {
  if (h.kind === 'airspace') return h.title;
  return h.ident;
}

function rowSubtitle(h: LongPressHit): string {
  if (h.kind === 'airspace') return h.subtitle;
  // SearchHit name + sub
  const parts: string[] = [];
  if (h.name) parts.push(h.name);
  if (h.sub) parts.push(h.sub);
  return parts.join(' · ');
}

/** Hemisphere-prefixed decimal-degrees lat/lon — e.g. "N 37.7749°  W 122.4194°".
 *  Decimal degrees stay legible on a small popup; pilots scanning the
 *  picker know what to expect from ForeFlight's "drop pin" affordance. */
function formatCoord(lat: number, lon: number): string {
  const latH = lat >= 0 ? 'N' : 'S';
  const lonH = lon >= 0 ? 'E' : 'W';
  return `${latH} ${Math.abs(lat).toFixed(4)}°  ${lonH} ${Math.abs(lon).toFixed(4)}°`;
}
